// /api/v1 (型付き JSON レイヤ) のブラウザ側クライアント。
//
// サーバ側 internal/api の転送形式に 1:1 で対応する:
//   - 成功: 2xx + 素の DTO ({"ok":true} は付かない)
//   - 失敗: 非 2xx + {"ok":false,"error":"..."} の封筒 → ApiError を throw
//
// SSR の fetch (フォーム POST 相当) と違い JSON body には _csrf を混ぜられないので、
// 非 GET は X-CSRF-Token ヘッダーで token を送る (サーバ: auth.CSRFHeaderName)。
// token の取得は csrf.ts の唯一の実装に任せる。

import { u } from "./url";
import { csrfToken } from "./csrf";

/** 非 2xx のとき throw される。message はサーバの封筒 error (日本語の表示可能文言)。 */
export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

/** 失敗時だけ返る封筒。成功時のレスポンスには ok も error も無い。 */
interface ErrEnvelope {
  ok?: boolean;
  error?: string;
}

/**
 * /api/v1 を叩いて DTO を返す。method 省略時は body の有無で GET/POST を決める。
 * 非 2xx は ApiError を throw するので、呼び出し側は成功パスだけ書けばよい。
 */
export async function apiFetch<T>(
  path: string,
  init?: { method?: string; body?: unknown },
): Promise<T> {
  const body = init?.body;
  const hasBody = body !== undefined;
  const method = init?.method || (hasBody ? "POST" : "GET");
  const headers: Record<string, string> = { Accept: "application/json" };
  if (hasBody) headers["Content-Type"] = "application/json";
  if (method !== "GET" && method !== "HEAD") headers["X-CSRF-Token"] = csrfToken();

  const res = await fetch(u(path), {
    method,
    credentials: "same-origin",
    headers,
    body: hasBody ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    let msg = "";
    try {
      const env = (await res.json()) as ErrEnvelope;
      msg = env.error || "";
    } catch {
      // 封筒が返らない異常系 (proxy の 502、ネットワーク断でボディが空 等)
    }
    throw new ApiError(res.status, msg || "HTTP " + res.status);
  }
  return (await res.json()) as T;
}

// ===== エンドポイントごとの型 (Go の DTO と 1:1) =====

/** handler.tlRefreshItem。omitempty の付いたフィールドだけ optional。 */
export interface TimelineRefreshPost {
  id: number;
  user_id: number;
  body: string;
  created_at: string;
  image_url?: string;
  handle: string;
  display_name?: string;
  avatar?: string;
  like_count: number;
  repost_count: number;
  reply_count: number;
}

/** GET /api/v1/timeline/refresh — handler.tlRefreshResp。 */
export interface TimelineRefreshResp {
  count: number;
  html: string;
  latest_id: number;
  posts: TimelineRefreshPost[];
}

/** handler.onbStep。 */
export interface OnboardingStep {
  key: string;
  title: string;
  desc: string;
  href: string;
  help: string;
  done: boolean;
}

/**
 * GET /api/v1/onboarding/state — handler.onbStateResp。
 * ガイド対象外のユーザーは active=false / steps=null で返る。
 */
export interface OnboardingStateResp {
  active: boolean;
  welcomeSeen: boolean;
  completed: boolean;
  steps: OnboardingStep[] | null;
  doneCount: number;
  total: number;
}

/**
 * POST /api/v1/onboarding/step — handler.onbStepResp。
 * active=false は「もう表示しない」済み / 対象外で記録しなかったことを表す。
 */
export interface OnboardingStepResp {
  active: boolean;
}
